<template>
    <div class="">
        <h2>Details for Audit</h2>

        <!-- View Resource that this edit is linked to. -->
        <div>
            <button-blue-secondary      v-if="canViewModel"
                                        class="mb-4"
                                        @click="viewModel">
                View Resource
            </button-blue-secondary>
        </div>

        <!-- View Parent Audit if this is a propagated change. -->
        <div>
            <button-blue-secondary      v-if="isPropagatedChange"
                                        class="mb-4"
                                        @click="viewPropagatingAudit">
                View Triggering Change
            </button-blue-secondary>
        </div>

        <!-- Standard Audit Entry -->
        <div>
            <h3     class="text-left mb-1 pb-1">Changes on: {{ audit.auditable_name_pretty }}</h3>

            <tailwind-table :data="data"
                            :exportable="false"
                            :headers="headers"
            >
                <!-- Field -->
                <template   #col-1="props">
                    <div    class="flex flex-col items-start justify-center">
                    <span   class="my-1 text-gray-700">
                        {{ props.row.field }}
                    </span>
                    </div>
                </template>

                <!-- Old Value -->
                <template   #col-2="props">
                    <div    :class="[props.row.old_value === '-' ? 'items-center' : 'items-start']"
                            class="flex flex-col justify-center w-full">
                        <div    v-if="hasOldAndNewValues">
                            <!-- Show old line by line -->
                            <div    v-for="text in oldReadableLineSplit[props.row.field]"
                                    class="py-2">
                                <span   class="my-1 text-gray-700">
                                    {{ text }}
                                </span>
                            </div>
                        </div>
                        <div    v-else>
                            <!-- Show Changes line by line -->
                            <div    v-for="text in humanReadableLineSplit[props.row.field]"
                                    class="py-2">
                                <span   class="my-1 text-gray-700">
                                    {{ text }}
                                </span>
                            </div>
                        </div>
                    </div>
                </template>

                <!-- New Value -->
                <template   #col-3="props">
                    <div    :class="[props.row.new_value === '-' ? 'items-center' : 'items-start']"
                            class="flex flex-col items-start justify-center w-full">
                        <!-- Show New Values line by line -->
                        <div    v-for="text in newReadableLineSplit[props.row.field]"
                                class="py-2">
                            <span   class="my-1 text-gray-700">
                                {{ text }}
                            </span>
                        </div>
                    </div>
                </template>

                <!-- Changes Human Readable -->
                <template   #col-4="props">
                    <div    :class="[props.row.changes_human_readable === '-' ? 'items-center' : 'items-start']"
                            class="flex flex-col items-start justify-center w-full">
                        <!-- Show Changes line by line -->
                        <div    v-for="text in humanReadableLineSplit[props.row.field]"
                                class="py-2">
                            <span   class="my-1 text-gray-700">
                                {{ text }}
                            </span>
                        </div>
                    </div>
                </template>
            </tailwind-table>
        </div>

        <div>
            <!-- Meta Information -->
            <h3     class="text-left mb-1 pb-1">Meta Information</h3>
            <tailwind-table :data="metaData"
                            :exportable="false"
                            :headers="headersForMetaInformation">
            </tailwind-table>
        </div>
    </div>
</template>

<script>
import axios from "axios"
import _ from 'lodash'
import {SonicuCoreMixin} from '@/mixins/SonicuCoreMixin'
import {iconNamesMixin} from '@/mixins/IconNamesMixin';
import TailwindTable from "@/components/elements/TailwindTable.vue";
import ButtonBlueSecondary from "@/components/elements/buttons/ButtonBlueSecondary.vue";

export default {
    name: "EnhancedAuditDetails",
    mixins: [SonicuCoreMixin, iconNamesMixin],
    components: {ButtonBlueSecondary, TailwindTable},
    props: {
        audit: {
            type: Object,
            required: true,
        }
    },
    data() {
        return {
            data: [],
            metaData: [],
            headers: null,
            headersWithoutOldAndNewValues: [
                {
                    name: 'Field',
                    sortBy: 'field',
                    show: 'sm',
                },
                {
                    name: 'Human Readable Changes',
                    sortBy: 'changes_human_readable',
                    show: 'sm',
                },
            ],
            headersWithOldAndNewValues: [
                {
                    name: 'Field',
                    sortBy: 'field',
                    show: 'sm',
                },
                {
                    name: 'Old Value',
                    sortBy: 'old_value',
                    show: 'sm',
                },
                {
                    name: 'New Value',
                    sortBy: 'new_value',
                    show: 'sm',
                },
                {
                    name: 'Human Readable Changes',
                    sortBy: 'changes_human_readable',
                    show: 'sm',
                },
            ],
            headersForMetaInformation: [
                {
                    name: 'Property',
                    sortBy: 'property',
                    key: 'property',
                    show: 'sm',
                },
                {
                    name: 'Value',
                    sortBy: 'value',
                    key: 'value',
                    show: 'sm',
                },
            ],
            requestNumber: 0,

            // Human Readable Line Split
            oldReadableLineSplit: [],
            newReadableLineSplit: [],
            humanReadableLineSplit: [],

            // swap between a table w/ old and new values vs field & human-readable changes
            hasOldAndNewValues: false,
        }
    },
    created() {

    },
    mounted() {
        this.refresh()
        this.updateHumanReadableLineSplit()

        this.metaData = [
            {
                property: 'Model Changed',
                value: this.audit.auditable_type_pretty,
            },
            {
                property: 'Edited by User',
                value: this.audit.user_display_name,
            },
            {
                property: 'Event',
                value: this.audit.event,
            },
            {
                property: 'Changed Page',
                value: this.audit.url,
            },
            {
                property: 'User-Agent',
                value: this.audit.user_agent,
            },
            {
                property: 'IP Address',
                value: this.audit.ip_address,
            },
        ]
    },
    methods: {
        refresh() {
            if (_.isNil(this.audit)) {
                console.log(`No audit selected`)
                return
            }

            this.limitHeadersWithOldAndNewValues()

            // Populate the data that will be displayed in the table
            this.data = []
            _.forEach(this.audit.changes, (item) => {
                this.data.push({
                    field: item,
                    old_value: this.valueFromArray(this.audit.old_human_readable, item),
                    new_value: this.valueFromArray(this.audit.new_human_readable, item),
                    changes_human_readable: this.valueFromArray(this.audit.changes_human_readable, item),
                })
            })

        },
        updateHumanReadableLineSplit() {
            this.humanReadableLineSplit = {}
            this.humanReadableLineSplit = this.lineSplitFromAttributes(this.audit.changes_human_readable)

            this.oldReadableLineSplit = {}
            this.oldReadableLineSplit = this.lineSplitFromAttributes(this.audit.old_human_readable)
            // this.oldReadableLineSplit = this.lineSplitFromAttributes(this.audit.old_values)

            this.newReadableLineSplit = {}
            this.newReadableLineSplit = this.lineSplitFromAttributes(this.audit.new_human_readable)
            // this.newReadableLineSplit = this.lineSplitFromAttributes(this.audit.new_values)
        },
        lineSplitFromAttributes(attributes) {
            let attributeLineSplit = {}

            _.each(attributes, (attributeValue, attributeName) => {
                let value = []
                if (_.isString(attributeValue)) {
                    value = this.haveCommasWithAlphanumericCharacterFollowingAddSpace(attributeValue)
                    value = value.split('\n')
                }
                else if (_.isNumber(attributeValue)) {
                    value = [attributeValue]
                }
                else {
                    value = attributeValue
                }

                attributeLineSplit[attributeName] = value
            })

            return attributeLineSplit
        },
        haveCommasWithAlphanumericCharacterFollowingAddSpace(text) {
            let regex = /,/g
            return _.replace(text, regex, ', ')
        },
        limitHeadersWithOldAndNewValues() {
            // If old and new Values don't exist, this is a propagated change, only show the field and human-readable
            let oldValuesExist = this.audit.old_values !== null
            let newValuesExist = this.audit.new_values !== null
            this.hasOldAndNewValues = oldValuesExist && newValuesExist

            this.headers = this.hasOldAndNewValues
                ? this.headersWithOldAndNewValues
                : this.headersWithoutOldAndNewValues
        },
        valueFromArray(obj, key) {
            // If obj is null return '-'
            if (_.isNil(obj)) {
                return '-'
            }

            // TODO> Why are no old and new values found for notification time var.
            // Ensure that the key is in the object
            let index = Object.keys(obj).indexOf(key)
            if (index === -1) {
                return '-'
            }

            return obj[key]
        },
        viewModel() {
            if (! this.canViewModel) {
                return
            }

            // TODO. Go to Model via link provided in Resource.
            window.open(this.audit.link, '_blank')
        },
        viewPropagatingAudit(newTab = false) {
            let route = `enhanced-audits?id=${this.audit.triggering_audit_id}&`
            if (newTab) {
                window.open(route, '_blank')
            }
            else {
                window.open(route)
            }
        },
    },
    computed: {
        canViewModel() {
            return ! _.isNil(this.audit) && ! _.isNil(this.audit.link)
        },
        isPropagatedChange() {
            return this.audit.triggering_audit_id !== null
        },
    },
    watch: {
        audit() {
            this.refresh()
        }
    },
}
</script>

<style scoped>

</style>